import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { tokenStore } from '../lib/tokenStore';
import { kpiAPI } from '@/lib/api';
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, X, Check, Info, AlertCircle, Plus, Edit2, Trash2, Settings, ArrowLeft, ImagePlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const DAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const CATEGORIES = [
  { value: 'default', label: 'Standard', color: 'bg-slate-500' },
  { value: 'neu', label: 'NEU', color: 'bg-orange-500' },
  { value: 'muay_thai', label: 'Muay Thai', color: 'bg-orange-500' },
  { value: 'kids', label: 'Kids', color: 'bg-emerald-500' },
  { value: 'kids_neu', label: 'Kids NEU', color: 'bg-emerald-500' },
  { value: 'luta_livre', label: 'Luta Livre', color: 'bg-purple-500' },
  { value: 'ladies_boxing', label: 'Ladies Boxing', color: 'bg-pink-500' },
  { value: 'mma', label: 'MMA', color: 'bg-amber-500' },
  { value: 'sc', label: 'Strength & Conditioning', color: 'bg-blue-500' },
  { value: 'hiit', label: 'HIIT Training', color: 'bg-blue-500' },
  { value: 'soul_strength', label: 'Soul & Strength', color: 'bg-pink-500' },
  { value: 'fundamental', label: 'Fundamental', color: 'bg-amber-500' },
  { value: 'run', label: 'Community Run', color: 'bg-emerald-500' },
  { value: 'open_mat', label: 'Open Mat', color: 'bg-indigo-500' },
  { value: 'special', label: 'Special', color: 'bg-pink-500' },
];

const CATEGORY_STYLES = {
  neu: { border: 'border-l-orange-500', bg: 'bg-orange-500/8', badge: 'bg-orange-500 text-white', label: 'NEU' },
  muay_thai: { border: 'border-l-orange-500', bg: 'bg-orange-500/8', badge: 'bg-orange-500 text-white', label: 'MUAY THAI' },
  kids: { border: 'border-l-emerald-500', bg: 'bg-emerald-500/8', badge: 'bg-emerald-500 text-white', label: 'KIDS' },
  kids_neu: { border: 'border-l-emerald-500', bg: 'bg-emerald-500/8', badge: 'bg-orange-500 text-white', label: 'KIDS NEU' },
  luta_livre: { border: 'border-l-purple-500', bg: 'bg-purple-500/8', badge: 'bg-purple-500 text-white', label: 'LUTA LIVRE' },
  ladies_boxing: { border: 'border-l-pink-500', bg: 'bg-pink-500/8', badge: 'bg-pink-500 text-white', label: 'LADIES' },
  mma: { border: 'border-l-amber-500', bg: 'bg-amber-500/8', badge: 'bg-amber-500 text-white', label: 'MMA' },
  sc: { border: 'border-l-blue-500', bg: 'bg-blue-500/8', badge: 'bg-blue-500 text-white', label: 'S&C' },
  soul_strength: { border: 'border-l-pink-500', bg: 'bg-pink-500/8', badge: 'bg-pink-500 text-white', label: 'SOUL' },
  fundamental: { border: 'border-l-amber-500', bg: 'bg-amber-500/8', badge: 'bg-amber-500 text-white', label: 'FUNDAMENTAL' },
  run: { border: 'border-l-emerald-500', bg: 'bg-emerald-500/8', badge: 'bg-emerald-500 text-white', label: 'RUN' },
  open_mat: { border: 'border-l-indigo-500', bg: 'bg-indigo-500/8', badge: 'bg-indigo-500 text-white', label: 'OPEN MAT' },
  special: { border: 'border-l-pink-500', bg: 'bg-pink-500/8', badge: 'bg-pink-500 text-white', label: 'SPECIAL' },
  default: { border: 'border-l-slate-400', bg: 'bg-slate-500/5', badge: '', label: '' },
};

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatWeekDate(monday) {
  return monday.toISOString().split('T')[0];
}

function formatDateRange(monday) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts = { day: '2-digit', month: '2-digit' };
  return `${monday.toLocaleDateString('de-DE', opts)} - ${sunday.toLocaleDateString('de-DE', opts)}`;
}

function getDayDate(monday, dayIndex) {
  const d = new Date(monday);
  d.setDate(monday.getDate() + dayIndex);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

export default function KursplanPage() {
  const { user } = useAuth();
  const [view, setView] = useState('schedule'); // 'schedule' | 'admin'
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  return view === 'admin' && isAdmin ? (
    <CourseAdmin onBack={() => setView('schedule')} />
  ) : (
    <ScheduleView onAdmin={isAdmin ? () => setView('admin') : null} />
  );
}


function ScheduleView({ onAdmin }) {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [currentMonday, setCurrentMonday] = useState(getMonday(new Date()));
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [detailCourse, setDetailCourse] = useState(null);
  const [courseBookings, setCourseBookings] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const weekDate = formatWeekDate(currentMonday);
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const getHeaders = () => ({
    ...tokenStore.getAuthHeaders(),
    'Content-Type': 'application/json',
  });
  // Bearer-Token lebt nur im Speicher (siehe tokenStore.js) und ist nach einem
  // Reload leer, bis erneut eingeloggt wird — die Session läuft dann über den
  // httpOnly-Cookie weiter. fetch() sendet Cookies aber nur mit 'include'.
  const AUTH_FETCH = { credentials: 'include' };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, bookingsRes] = await Promise.all([
        fetch(`${API}/api/kursplan/courses?week_date=${weekDate}`, { headers: getHeaders(), ...AUTH_FETCH }),
        fetch(`${API}/api/kursplan/bookings/my?week_date=${weekDate}`, { headers: getHeaders(), ...AUTH_FETCH }),
      ]);
      if (coursesRes.ok) {
        const data = await coursesRes.json();
        setCourses(data.courses || []);
      }
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setMyBookings(data.bookings || []);
      }
    } catch {
      toast.error('Fehler beim Laden des Kursplans');
    }
    setLoading(false);
  }, [weekDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBook = async (course) => {
    setBookingLoading(true);
    try {
      const res = await fetch(`${API}/api/kursplan/book`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ course_id: course.id, week_date: weekDate }),
        ...AUTH_FETCH,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Gebucht: ${course.name} (${course.day})`);
        setSelectedCourse(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Buchung fehlgeschlagen');
      }
    } catch {
      toast.error('Netzwerkfehler');
    }
    setBookingLoading(false);
  };

  const handleCancel = async (bookingId) => {
    try {
      const res = await fetch(`${API}/api/kursplan/book/${bookingId}`, {
        method: 'DELETE', headers: getHeaders(), ...AUTH_FETCH,
      });
      if (res.ok) {
        toast.success('Buchung storniert');
        setSelectedCourse(null);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Stornierung fehlgeschlagen');
      }
    } catch {
      toast.error('Netzwerkfehler');
    }
  };

  const loadCourseDetail = async (course) => {
    setDetailCourse(course);
    try {
      const res = await fetch(
        `${API}/api/kursplan/bookings/course/${course.id}?week_date=${weekDate}`,
        { headers: getHeaders(), ...AUTH_FETCH }
      );
      if (res.ok) {
        const data = await res.json();
        setCourseBookings(data.bookings || []);
      }
    } catch (err) {  }
  };

  const loadCourseQr = async (course) => {
    setQrLoading(true);
    try {
      const res = await fetch(`${API}/api/kursplan/qr/${course.id}`, { headers: getHeaders(), ...AUTH_FETCH });
      const data = await res.json();
      if (res.ok) {
        setQrData(data);
      } else {
        toast.error(data.detail || 'QR-Code konnte nicht geladen werden');
      }
    } catch {
      toast.error('Netzwerkfehler');
    }
    setQrLoading(false);
  };

  const handleAusfall = async (course, datum, grund) => {
    try {
      const r = (await kpiAPI.kursAusfall(course.id, { datum, grund })).data;
      toast.success(`Ausfall gemeldet — ${r.buchungen_storniert} Buchungen storniert, ${r.push_gesendet} Push`);
      setDetailCourse(null); setCourseBookings([]); fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ausfall konnte nicht gemeldet werden'); }
  };
  const handleAusfallAufheben = async (course, datum) => {
    try { await kpiAPI.kursAusfallAufheben(course.id, datum); toast.success('Kurs findet wieder statt'); setDetailCourse(null); setCourseBookings([]); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };
  const prevWeek = () => { const d = new Date(currentMonday); d.setDate(d.getDate() - 7); setCurrentMonday(d); };
  const nextWeek = () => { const d = new Date(currentMonday); d.setDate(d.getDate() + 7); setCurrentMonday(d); };
  const goToday = () => setCurrentMonday(getMonday(new Date()));

  const isBooked = (courseId) => myBookings.some(b => b.course_id === courseId);
  const getBooking = (courseId) => myBookings.find(b => b.course_id === courseId);

  const coursesByDay = DAYS.map((day, i) =>
    courses.filter(c => c.day_index === i).sort((a, b) => a.start.localeCompare(b.start))
  );

  return (
    <div className="space-y-4" data-testid="kursplan-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="kursplan-title">Kursplan</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Neuer Kursplan ab April 2026 &middot; {courses.length} Kurse
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onAdmin && (
            <button
              onClick={onAdmin}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              data-testid="admin-courses-btn"
            >
              <Settings className="w-3.5 h-3.5" />
              Kurse verwalten
            </button>
          )}
          <button onClick={goToday} className="px-3 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-accent transition-colors" data-testid="today-btn">
            Heute
          </button>
          <div className="flex items-center bg-card border border-border rounded-lg">
            <button onClick={prevWeek} className="p-1.5 hover:bg-accent rounded-l-lg transition-colors" data-testid="prev-week-btn">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1.5 text-sm font-medium min-w-[160px] text-center" data-testid="week-range">
              {formatDateRange(currentMonday)}
            </span>
            <button onClick={nextWeek} className="p-1.5 hover:bg-accent rounded-r-lg transition-colors" data-testid="next-week-btn">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {myBookings.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3" data-testid="my-bookings-summary">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">Meine Buchungen diese Woche ({myBookings.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {myBookings.map(b => (
              <span key={b.id} className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2.5 py-1 rounded-full text-xs font-medium">
                <span>{b.day.slice(0, 2)}</span>
                <span>{b.start}</span>
                <span className="opacity-60">|</span>
                <span className="max-w-[120px] truncate">{b.course_name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleCancel(b.id); }}
                  className="ml-0.5 hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                  title="Stornieren"
                  data-testid={`cancel-booking-${b.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="hidden lg:grid grid-cols-7 gap-2" data-testid="schedule-grid-desktop">
            {DAYS.map((day, i) => (
              <div key={day} className="space-y-1.5">
                <div className="text-center py-2 bg-card border border-border rounded-lg">
                  <div className="text-xs font-bold uppercase tracking-wider text-foreground">{day}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{getDayDate(currentMonday, i)}</div>
                </div>
                <div className="space-y-1.5 min-h-[400px]">
                  {coursesByDay[i].map(course => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      booked={isBooked(course.id)}
                      booking={getBooking(course.id)}
                      onBook={() => setSelectedCourse(course)}
                      onCancel={() => { const b = getBooking(course.id); if (b) handleCancel(b.id); }}
                      onDetail={isAdmin ? () => loadCourseDetail(course) : null}
                      compact
                    />
                  ))}
                  {coursesByDay[i].length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-8 opacity-50">Keine Kurse</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:hidden space-y-3" data-testid="schedule-grid-mobile">
            {DAYS.map((day, i) => (
              coursesByDay[i].length > 0 && (
                <div key={day}>
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-background z-10 py-1">
                    <span className="text-sm font-bold">{day}</span>
                    <span className="text-xs text-muted-foreground">{getDayDate(currentMonday, i)}</span>
                    <div className="flex-1 border-t border-border" />
                  </div>
                  <div className="space-y-1.5">
                    {coursesByDay[i].map(course => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        booked={isBooked(course.id)}
                        booking={getBooking(course.id)}
                        onBook={() => setSelectedCourse(course)}
                        onCancel={() => { const b = getBooking(course.id); if (b) handleCancel(b.id); }}
                        onDetail={isAdmin ? () => loadCourseDetail(course) : null}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </>
      )}

      <div className="flex items-start gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>* Open Mat (freies Training) findet alle zwei Wochen statt. Plätze pro Kurs variieren je nach Kurseinstellung.</span>
      </div>

      {selectedCourse && (
        <BookingDialog
          course={selectedCourse}
          booked={isBooked(selectedCourse.id)}
          booking={getBooking(selectedCourse.id)}
          loading={bookingLoading}
          onBook={() => handleBook(selectedCourse)}
          onCancel={() => { const b = getBooking(selectedCourse.id); if (b) handleCancel(b.id); }}
          onClose={() => setSelectedCourse(null)}
        />
      )}

      {detailCourse && (
        <CourseDetailDialog
          course={detailCourse}
          bookings={courseBookings}
          isAdmin={isAdmin}
          qrLoading={qrLoading}
          onShowQr={() => loadCourseQr(detailCourse)}
          onClose={() => { setDetailCourse(null); setCourseBookings([]); }}
          onCancelBooking={handleCancel}
          weekDate={weekDate}
          onAusfall={handleAusfall}
          onAusfallAufheben={handleAusfallAufheben}
          onEingetragen={() => { loadCourseDetail(detailCourse); fetchData(); }}
        />
      )}

      {qrData && (
        <CourseQrDialog qrData={qrData} onClose={() => setQrData(null)} />
      )}
    </div>
  );
}


function CourseQrDialog({ qrData, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="course-qr-dialog">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-1">{qrData.course_name}</h3>
        <p className="text-sm text-muted-foreground mb-4">{qrData.day} &middot; {qrData.start} - {qrData.end} Uhr</p>
        <img src={qrData.qr_code} alt="Check-in QR-Code" className="w-56 h-56 mx-auto rounded-lg border border-border" />
        <p className="text-xs text-muted-foreground mt-4">
          Ausdrucken und im Studio aushängen — Mitglieder scannen diesen Code in der App, um sich direkt für diesen Kurs einzuchecken.
        </p>
        <button
          onClick={() => window.print()}
          className="w-full mt-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          Drucken
        </button>
        <button
          onClick={onClose}
          className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
        >
          Schließen
        </button>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────
// Course Admin Panel
// ──────────────────────────────────────────────

function CourseAdmin({ onBack }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCourse, setEditCourse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [filterDay, setFilterDay] = useState('');

  const getHeaders = () => ({
    ...tokenStore.getAuthHeaders(),
    'Content-Type': 'application/json',
  });
  const AUTH_FETCH = { credentials: 'include' };

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/kursplan/admin/courses`, { headers: getHeaders(), ...AUTH_FETCH });
      if (res.ok) {
        const data = await res.json();
        setCourses(data.courses || []);
      }
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleDelete = async (course) => {
    if (!window.confirm(`Kurs "${course.name}" wirklich löschen?`)) return;
    try {
      const res = await fetch(`${API}/api/kursplan/admin/courses/${course.id}`, {
        method: 'DELETE', headers: getHeaders(), ...AUTH_FETCH,
      });
      if (res.ok) {
        toast.success('Kurs gelöscht');
        fetchCourses();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Fehler');
      }
    } catch { toast.error('Netzwerkfehler'); }
  };

  const filtered = courses.filter(c => {
    if (!c.active) return false;
    if (filterDay && c.day !== filterDay) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = DAYS.map(day => ({
    day,
    courses: filtered.filter(c => c.day === day).sort((a, b) => a.start.localeCompare(b.start)),
  })).filter(g => g.courses.length > 0);

  return (
    <div className="space-y-4" data-testid="course-admin-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-accent rounded-lg transition-colors" data-testid="back-to-schedule-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Kurse verwalten</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{filtered.length} aktive Kurse</p>
          </div>
        </div>
        <button
          onClick={() => { setEditCourse(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          data-testid="add-course-btn"
        >
          <Plus className="w-4 h-4" />
          Neuen Kurs anlegen
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Kurs suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm w-64"
          data-testid="course-search-input"
        />
        <select
          value={filterDay}
          onChange={(e) => setFilterDay(e.target.value)}
          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
          data-testid="course-day-filter"
        >
          <option value="">Alle Tage</option>
          {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ day, courses: dayCourses }) => (
            <div key={day}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold">{day}</span>
                <span className="text-xs text-muted-foreground">({dayCourses.length} Kurse)</span>
                <div className="flex-1 border-t border-border" />
              </div>
              <div className="space-y-1.5">
                {dayCourses.map(course => {
                  const cat = CATEGORIES.find(c => c.value === course.category) || CATEGORIES[0];
                  const style = CATEGORY_STYLES[course.category] || CATEGORY_STYLES.default;
                  return (
                    <div
                      key={course.id}
                      className={`flex items-center justify-between border-l-[3px] ${style.border} ${style.bg} rounded-r-lg px-4 py-3 group`}
                      data-testid={`admin-course-${course.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <KursFoto course={course} onGeaendert={fetchCourses} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold truncate">{course.name}</p>
                            {style.label && (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${style.badge}`}>{style.label}</span>
                            )}
                            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{cat.label}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {course.start} - {course.end} Uhr
                            </span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Users className="w-3 h-3" /> max. {course.max_spots} Plätze
                            </span>
                            <span className={`text-xs ${course.trainer ? 'text-muted-foreground' : 'text-amber-600'}`}>
                              {course.trainer ? `Trainer: ${course.trainer}` : 'Kein Trainer'}
                              {course.assistenz ? ` · Assistenz: ${course.assistenz}` : ''}
                            </span>
                            {course.note && (
                              <span className="text-[10px] text-amber-600 italic">* {course.note}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => { setEditCourse(course); setShowForm(true); }}
                          className="p-1.5 hover:bg-accent rounded-lg transition-colors"
                          title="Bearbeiten"
                          data-testid={`edit-course-${course.id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(course)}
                          className="p-1.5 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                          title="Löschen"
                          data-testid={`delete-course-${course.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">Keine Kurse gefunden</p>
          )}
        </div>
      )}

      {showForm && (
        <CourseFormDialog
          course={editCourse}
          onClose={() => { setShowForm(false); setEditCourse(null); }}
          onSuccess={() => { setShowForm(false); setEditCourse(null); fetchCourses(); }}
        />
      )}
    </div>
  );
}


// Kursfoto je Kursart (14.10.2026, backend/kursbilder.py): gilt für alle Termine mit demselben Kursnamen und
// erscheint in der App (Kursliste, Empfehlungen). Der Server verkleinert und entfernt Metadaten.
function KursFoto({ course, onGeaendert }) {
  const [laeuft, setLaeuft] = useState(false);
  const pfad = `${API}/api/kursplan/admin/courses/${course.id}/bild`;

  const hochladen = async (e) => {
    const datei = e.target.files?.[0];
    e.target.value = '';
    if (!datei) return;
    setLaeuft(true);
    try {
      const form = new FormData();
      form.append('datei', datei);
      const res = await fetch(pfad, { method: 'POST', headers: tokenStore.getAuthHeaders(), credentials: 'include', body: form });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Foto gespeichert – gilt für alle Termine von „${course.name}“`);
        onGeaendert();
      } else toast.error(data.detail || 'Foto nicht gespeichert');
    } catch { toast.error('Netzwerkfehler'); }
    setLaeuft(false);
  };

  const entfernen = async () => {
    if (!window.confirm(`Foto für alle Termine von „${course.name}“ entfernen?`)) return;
    setLaeuft(true);
    try {
      const res = await fetch(pfad, { method: 'DELETE', headers: tokenStore.getAuthHeaders(), credentials: 'include' });
      if (res.ok) { toast.success('Foto entfernt'); onGeaendert(); }
      else toast.error((await res.json().catch(() => ({}))).detail || 'Fehler');
    } catch { toast.error('Netzwerkfehler'); }
    setLaeuft(false);
  };

  return (
    <div className="flex flex-col items-center gap-0.5 shrink-0" data-testid={`kurs-foto-${course.id}`}>
      <label
        className={`w-12 h-12 rounded-md border border-dashed border-input overflow-hidden flex items-center justify-center cursor-pointer hover:bg-accent ${laeuft ? 'opacity-50 pointer-events-none' : ''}`}
        title={course.bild_url ? 'Foto ersetzen' : 'Foto hochladen'}
      >
        {course.bild_url
          ? <img src={`${API}${course.bild_url}`} alt="" className="w-full h-full object-cover" />
          : <ImagePlus className="w-4 h-4 text-muted-foreground" />}
        <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" onChange={hochladen}
          data-testid={`kurs-foto-input-${course.id}`} />
      </label>
      {course.bild_url
        ? <button type="button" onClick={entfernen} disabled={laeuft} className="text-[9px] text-destructive hover:underline"
            data-testid={`kurs-foto-entfernen-${course.id}`}>Entfernen</button>
        : <span className="text-[9px] text-muted-foreground">Foto</span>}
    </div>
  );
}

function CourseFormDialog({ course, onClose, onSuccess }) {
  const isEdit = !!course;
  const [name, setName] = useState(course?.name || '');
  const [day, setDay] = useState(course?.day || 'Montag');
  const [start, setStart] = useState(course?.start || '');
  const [end, setEnd] = useState(course?.end || '');
  const [category, setCategory] = useState(course?.category || 'default');
  const [maxSpots, setMaxSpots] = useState(String(course?.max_spots || 16));
  const [trialSlots, setTrialSlots] = useState(String(course?.trial_slots || 0));
  const [note, setNote] = useState(course?.note || '');
  const [trainer, setTrainer] = useState(course?.trainer || '');
  const [assistenz, setAssistenz] = useState(course?.assistenz || '');
  const [ersatz, setErsatz] = useState(course?.ersatz || '');
  const [trainerNamen, setTrainerNamen] = useState([]);
  const [saving, setSaving] = useState(false);

  // Vorschläge für „Trainer": alle mit Trainer-Zugang in der App, damit der Name genau passt
  useEffect(() => {
    let aktiv = true;
    fetch(`${API}/api/kursplan/admin/trainer-namen`, { headers: tokenStore.getAuthHeaders(), credentials: 'include' })
      .then(r => (r.ok ? r.json() : { namen: [] }))
      .then(d => { if (aktiv) setTrainerNamen(d.namen || []); })
      .catch(() => {});
    return () => { aktiv = false; };
  }, []);

  const getHeaders = () => ({
    ...tokenStore.getAuthHeaders(),
    'Content-Type': 'application/json',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !start || !end) {
      toast.error('Name, Startzeit und Endzeit sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), day, start, end, category, max_spots: parseInt(maxSpots, 10) || 16, trial_slots: parseInt(trialSlots, 10) || 0, note: note.trim(), trainer: trainer.trim(), assistenz: assistenz.trim(), ersatz: ersatz.trim() };
      const url = isEdit
        ? `${API}/api/kursplan/admin/courses/${course.id}`
        : `${API}/api/kursplan/admin/courses`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
        credentials: 'include',
      });
      if (res.ok) {
        toast.success(isEdit ? 'Kurs aktualisiert' : 'Kurs erstellt');
        onSuccess();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Fehler beim Speichern');
      }
    } catch { toast.error('Netzwerkfehler'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="course-form-dialog">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{isEdit ? 'Kurs bearbeiten' : 'Neuen Kurs anlegen'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Kursname *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. BJJ Grundlagen"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
              data-testid="course-name-input"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tag *</label>
              <select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-day-select"
              >
                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Kategorie</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-category-select"
              >
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Start *</label>
              <input
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-start-input"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Ende *</label>
              <input
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-end-input"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Max. Plätze</label>
              <input
                type="number"
                min="1"
                max="100"
                value={maxSpots}
                onChange={(e) => setMaxSpots(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-max-spots-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Probetraining-Plätze</label>
              <input
                type="number"
                min="0"
                max="100"
                value={trialSlots}
                onChange={(e) => setTrialSlots(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-trial-slots-input"
                title="Reserviertes Kontingent für automatische Probetraining-Buchungen (0 = kein Kontingent)"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Trainer</label>
            <input
              type="text"
              value={trainer}
              onChange={(e) => setTrainer(e.target.value)}
              placeholder="z.B. Tina oder Tina, Max"
              list="kurs-trainer-namen"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
              data-testid="course-trainer-input"
            />
            <datalist id="kurs-trainer-namen">
              {trainerNamen.map(n => <option key={n} value={n} />)}
            </datalist>
            <p className="text-[11px] text-muted-foreground mt-1">
              Die Trainer sehen den Kurs in der App unter „Einsätze". Mehrere mit Komma trennen. Leer = alle Trainer sehen ihn.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Assistenz</label>
              <input
                type="text"
                value={assistenz}
                onChange={(e) => setAssistenz(e.target.value)}
                placeholder="z.B. Anna"
                list="kurs-trainer-namen"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-assistenz-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Ersatz</label>
              <input
                type="text"
                value={ersatz}
                onChange={(e) => setErsatz(e.target.value)}
                placeholder="z.B. Erik, Max"
                list="kurs-trainer-namen"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
                data-testid="course-ersatz-input"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Mehrere Namen mit Komma. Assistenz sieht den Kurs in der Trainer-App, Ersatz nur bei einer eingetragenen Vertretung.
          </p>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Anmerkung (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Findet alle zwei Wochen statt"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1"
              data-testid="course-note-input"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-sm font-medium rounded-lg border border-border hover:bg-accent transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="save-course-btn"
            >
              {saving ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Kurs anlegen'}
            </button>
          </div>
        </form>
        {isEdit && <VertretungAbschnitt course={course} ersatz={ersatz} trainerNamen={trainerNamen} />}
      </div>
    </div>
  );
}

/* Vertretung je Termin (19.10.2026, backend/kurs_vertretung.py): An einem Termin übernimmt jemand anderes. Der
   Haupttrainer sieht den Kurs an dem Tag nicht in der App, die Vertretung sieht ihn mit „Vertretung für …“. */
const zweistellig = (n) => String(n).padStart(2, '0');
const isoTag = (d) => `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;
const deTag = (iso) => { const [j, m, t] = String(iso).split('-'); return `${t}.${m}.${j}`; };

function naechsteTermine(day, anzahl = 12) {
  const ziel = (DAYS.indexOf(day) + 1) % 7;        // DAYS beginnt mit Montag, getDay() mit Sonntag
  if (ziel < 0 || DAYS.indexOf(day) < 0) return [];
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + ((ziel - d.getDay() + 7) % 7));
  return Array.from({ length: anzahl }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + 7 * i); return isoTag(x); });
}

function VertretungAbschnitt({ course, ersatz, trainerNamen }) {
  const termine = naechsteTermine(course.day);
  const [liste, setListe] = useState([]);
  const [datum, setDatum] = useState(termine[0] || '');
  const [wer, setWer] = useState('');
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [aufhebenId, setAufhebenId] = useState(null);
  const basis = `${API}/api/kursplan/admin/courses/${course.id}/vertretungen`;
  const kopf = () => ({ ...tokenStore.getAuthHeaders(), 'Content-Type': 'application/json' });

  const laden = useCallback(async () => {
    try {
      const r = await fetch(basis, { headers: tokenStore.getAuthHeaders(), credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (r.ok) setListe(d.vertretungen || []);
    } catch { /* Liste bleibt leer */ }
  }, [basis]);
  useEffect(() => { laden(); }, [laden]);

  const ersatzNamen = String(ersatz || '').split(',').map(n => n.trim()).filter(Boolean);
  const vorschlaege = [...ersatzNamen, ...trainerNamen.filter(n => !ersatzNamen.includes(n))];

  const eintragen = async () => {
    if (!datum || !wer.trim()) { toast.error('Bitte Termin und Trainer wählen'); return; }
    setLaeuft(true);
    try {
      const r = await fetch(basis, { method: 'POST', headers: kopf(), credentials: 'include',
        body: JSON.stringify({ datum, vertretung: wer.trim(), grund: grund.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Vertretung nicht eingetragen'); return; }
      toast.success(`Vertretung am ${deTag(datum)}: ${d.vertretung?.vertretung || wer.trim()}`);
      setWer(''); setGrund('');
      laden();
    } catch { toast.error('Netzwerkfehler'); } finally { setLaeuft(false); }
  };

  const aufheben = async (vid) => {
    try {
      const r = await fetch(`${basis}/${vid}`, { method: 'DELETE', headers: tokenStore.getAuthHeaders(), credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Fehler'); return; }
      toast.success('Vertretung aufgehoben');
      setAufhebenId(null);
      laden();
    } catch { toast.error('Netzwerkfehler'); }
  };

  const feld = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm mt-1';
  return (
    <div className="mt-5 pt-4 border-t border-border space-y-3" data-testid="kurs-vertretung">
      <div>
        <h4 className="text-sm font-semibold">Vertretung</h4>
        <p className="text-[11px] text-muted-foreground">
          Für einen einzelnen Termin. Der Haupttrainer hat an dem Tag frei, die Vertretung sieht den Kurs in der App.
        </p>
      </div>
      {liste.length > 0 ? (
        <ul className="space-y-1.5">
          {liste.map(v => (
            <li key={v.id} className="flex items-center justify-between gap-2 text-sm rounded-md border border-border px-3 py-2">
              <span className="min-w-0">
                <span className="font-medium">{deTag(v.datum)}</span> · {v.vertretung}
                {v.grund ? <span className="text-xs text-muted-foreground"> · {v.grund}</span> : null}
              </span>
              {aufhebenId === v.id ? (
                <span className="flex gap-1 shrink-0">
                  <button type="button" onClick={() => aufheben(v.id)}
                    className="text-xs px-2 py-1 rounded bg-destructive text-destructive-foreground" data-testid={`vertretung-aufheben-ja-${v.id}`}>
                    Ja, aufheben
                  </button>
                  <button type="button" onClick={() => setAufhebenId(null)} className="text-xs px-2 py-1 rounded border border-border">
                    Nein
                  </button>
                </span>
              ) : (
                <button type="button" onClick={() => setAufhebenId(v.id)}
                  className="text-xs px-2 py-1 rounded border border-border hover:bg-accent shrink-0" data-testid={`vertretung-aufheben-${v.id}`}>
                  Aufheben
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Keine Vertretung eingetragen.</p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Termin</label>
          <select value={datum} onChange={(e) => setDatum(e.target.value)} className={feld} data-testid="vertretung-datum">
            {termine.map(t => <option key={t} value={t}>{deTag(t)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Trainer</label>
          <input type="text" value={wer} onChange={(e) => setWer(e.target.value)} list="kurs-vertretung-namen"
            placeholder={ersatzNamen[0] ? `z.B. ${ersatzNamen[0]}` : 'Name'} className={feld} data-testid="vertretung-name" />
          <datalist id="kurs-vertretung-namen">
            {vorschlaege.map(n => <option key={n} value={n} />)}
          </datalist>
        </div>
      </div>
      {ersatzNamen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ersatzNamen.map(n => (
            <button key={n} type="button" onClick={() => setWer(n)}
              className={`text-xs px-2 py-1 rounded-full border ${wer === n ? 'border-primary text-primary' : 'border-border'}`}>
              Ersatz: {n}
            </button>
          ))}
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-muted-foreground">Grund (optional)</label>
        <input type="text" value={grund} onChange={(e) => setGrund(e.target.value)} maxLength={200}
          placeholder="z.B. Urlaub, krank" className={feld} data-testid="vertretung-grund" />
      </div>
      <button type="button" onClick={eintragen} disabled={laeuft}
        className="w-full py-2.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        data-testid="vertretung-eintragen">
        {laeuft ? 'Wird eingetragen …' : 'Vertretung eintragen'}
      </button>
    </div>
  );
}


// ──────────────────────────────────────────────
// Shared components (Course cards, dialogs)
// ──────────────────────────────────────────────

/* Belegung aus Buchungen UND Kursbestand (13.10.2026): Stammkunden buchen nicht, belegen aber einen Platz.
   Fehlen die Felder (ältere Antwort), gilt die reine Buchungszahl wie bisher. */
const belegung = (course) => {
  const erwartet = course.erwartet_spots ?? course.booked_spots;
  const frei = course.frei_erwartet ?? course.available_spots;
  const teile = [];
  if (course.booked_spots > 0) teile.push(`${course.booked_spots} gebucht`);
  if (course.stamm_ohne_buchung > 0) teile.push(`${course.stamm_ohne_buchung} Stammkunden`);
  if (course.stamm_abgesagt > 0) teile.push(`${course.stamm_abgesagt} abgesagt`);
  return { erwartet, frei, voll: frei <= 0, ueber: course.ueberbelegt || 0, text: teile.join(' · '), anteil: Math.min(100, (erwartet / (course.max_spots || 1)) * 100) };
};

function CourseCard({ course, booked, booking, onBook, onCancel, onDetail, compact }) {
  const style = CATEGORY_STYLES[course.category] || CATEGORY_STYLES.default;
  const b = belegung(course);
  const spotsLeft = b.frei;
  const isFull = b.voll;

  return (
    <div
      data-testid={`course-card-${course.id}`}
      onClick={onBook}
      className={`
        relative border-l-[3px] ${style.border} ${style.bg}
        rounded-r-lg p-2 cursor-pointer transition-all duration-150
        hover:shadow-md hover:scale-[1.01]
        ${booked ? 'ring-1 ring-primary/40' : ''}
        ${isFull && !booked ? 'opacity-60' : ''}
      `}
    >
      {style.label && (
        <span className={`absolute -top-1 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${style.badge}`}>
          {style.label}
        </span>
      )}
      {booked && (
        <span className="absolute -top-1 -left-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-primary-foreground" />
        </span>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`font-semibold leading-tight ${compact ? 'text-[11px]' : 'text-xs sm:text-sm'} ${course.ausfall ? 'line-through text-muted-foreground' : ''}`}>
            {course.name}{course.ausfall && <span className="ml-1 no-underline text-[10px] font-bold text-red-600">fällt aus</span>}
          </p>
          <div className={`flex items-center gap-1 text-muted-foreground mt-0.5 ${compact ? 'text-[10px]' : 'text-xs'}`}>
            <Clock className="w-3 h-3" />
            <span>{course.start} - {course.end} Uhr</span>
          </div>
          {course.note && (
            <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5 italic">* {course.note}</p>
          )}
          {(course.gueltig_ab || course.gueltig_bis) && (
            <p className="text-[9px] text-muted-foreground mt-0.5" data-testid="kurs-gueltigkeit">
              {course.gueltig_ab ? `ab ${new Date(course.gueltig_ab).toLocaleDateString('de-DE')}` : ''}
              {course.gueltig_ab && course.gueltig_bis ? ' · ' : ''}
              {course.gueltig_bis ? `bis ${new Date(course.gueltig_bis).toLocaleDateString('de-DE')}` : ''}
            </p>
          )}
        </div>
        {onDetail && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); onDetail(); }}
              className="p-1 hover:bg-accent rounded transition-colors"
              title="Details anzeigen"
              data-testid={`detail-btn-${course.id}`}
            >
              <Users className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="mt-1.5">
        <div className="flex items-center justify-between text-[10px] mb-0.5">
          <span className={`font-medium ${isFull ? 'text-destructive' : spotsLeft <= 3 ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {isFull ? (b.ueber > 0 ? `voll · ${b.ueber} über Plan` : 'voll belegt') : `${spotsLeft}/${course.max_spots} frei`}
          </span>
          {booked && <span className="text-primary font-semibold">Gebucht</span>}
        </div>
        {b.text && <p className="text-[9px] text-muted-foreground mb-0.5 truncate" data-testid={`course-belegung-${course.id}`}>{b.text}</p>}
        <div className="w-full h-1 bg-border rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isFull ? 'bg-destructive' : spotsLeft <= 3 ? 'bg-amber-500' : 'bg-primary'
            }`}
            style={{ width: `${b.anteil}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function BookingDialog({ course, booked, booking, loading, onBook, onCancel, onClose }) {
  const style = CATEGORY_STYLES[course.category] || CATEGORY_STYLES.default;
  const b = belegung(course);
  const spotsLeft = b.frei;
  const isFull = course.available_spots <= 0;        // gebucht werden kann, solange die Buchungsgrenze nicht erreicht ist

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="booking-dialog">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">{course.name}</h3>
            <p className="text-sm text-muted-foreground">{course.day} &middot; {course.start} - {course.end} Uhr</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {course.note && (
          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-500/10 px-3 py-2 rounded-lg mb-4">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{course.note}</span>
          </div>
        )}

        <div className="flex items-center justify-between py-3 px-3 bg-accent/50 rounded-lg mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">Verfügbare Plätze</span>
          </div>
          <span className={`text-sm font-bold ${b.voll ? 'text-destructive' : spotsLeft <= 3 ? 'text-amber-600' : 'text-primary'}`}>
            {spotsLeft} / {course.max_spots}
          </span>
        </div>
        <p className="text-xs text-muted-foreground -mt-2 mb-3">
          Erwartet: {b.erwartet} von {course.max_spots}{b.text ? ` (${b.text})` : ''}.{' '}
          {course.stamm_spots > 0 ? 'Stammkunden kommen aus dem Kursbestand.' : 'Für diesen Kurs sind im Kursbestand keine Stammkunden eingetragen.'}
          {b.ueber > 0 && <strong className="text-destructive"> {b.ueber} mehr als Plätze.</strong>}
        </p>

        <div className="w-full h-2 bg-border rounded-full overflow-hidden mb-4">
          <div
            className={`h-full rounded-full transition-all ${
              b.voll ? 'bg-destructive' : spotsLeft <= 3 ? 'bg-amber-500' : 'bg-primary'
            }`}
            style={{ width: `${b.anteil}%` }}
          />
        </div>

        {booked ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-primary bg-primary/10 px-3 py-2 rounded-lg">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Du bist für diesen Kurs gebucht</span>
            </div>
            <button
              onClick={onCancel}
              className="w-full py-2.5 px-4 text-sm font-medium rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
              data-testid="cancel-booking-btn"
            >
              Buchung stornieren
            </button>
          </div>
        ) : isFull ? (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Dieser Kurs ist leider ausgebucht</span>
          </div>
        ) : (
          <button
            onClick={onBook}
            disabled={loading}
            className="w-full py-2.5 px-4 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            data-testid="confirm-booking-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                Buchen...
              </span>
            ) : (
              'Jetzt buchen'
            )}
          </button>
        )}

        <button
          onClick={onClose}
          className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/* Mitglied für diesen Termin eintragen (14.10.2026) — nur Leitung, Suche wie am Empfang */
function MitgliedEintragen({ course, weekDate, onEingetragen }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [laeuft, setLaeuft] = useState(false);
  const kopf = () => ({ ...tokenStore.getAuthHeaders(), 'Content-Type': 'application/json' });
  useEffect(() => {
    if (q.trim().length < 2) { setTreffer([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/checkins/suche?q=${encodeURIComponent(q.trim())}&limit=6`, { headers: kopf(), credentials: 'include' });
        if (r.ok) setTreffer((await r.json()).treffer || []);
      } catch { /* Suche ist Komfort */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const eintragen = async (p) => {
    setLaeuft(true);
    try {
      const r = await fetch(`${API}/api/kursplan/admin/courses/${course.id}/eintragen`, {
        method: 'POST', headers: kopf(), credentials: 'include', body: JSON.stringify({ member_id: p.id, week_date: weekDate }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Eintragen fehlgeschlagen'); return; }
      toast.success(`${p.name} eingetragen${d.ohne_gastpass ? ' (ohne Gastpass)' : ''}`);
      // Kurskontingent (16.10.2026): das Studio wird nie blockiert — liegt die Person damit darüber, ein Hinweis
      if (d.ueber_kontingent) toast.warning(d.hinweis || 'Über Kontingent', { duration: 8000 });
      setQ(''); setTreffer([]);
      onEingetragen?.();
    } catch { toast.error('Netzwerkfehler'); } finally { setLaeuft(false); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="kurs-eintragen">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mitglied eintragen: Name suchen …"
        className="w-full text-sm rounded-md border border-border bg-background px-2 py-1.5" data-testid="kurs-eintragen-suche" />
      {treffer.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-2 py-1.5 px-3 bg-accent/30 rounded-lg">
          <span className="text-sm min-w-0 truncate">{p.name}{!p.mitglied && <span className="text-xs text-muted-foreground"> · kein Mitglied</span>}</span>
          <button type="button" disabled={laeuft} onClick={() => eintragen(p)} data-testid={`kurs-eintragen-${p.id}`}
            className="text-xs font-medium text-primary hover:underline flex-shrink-0">Eintragen</button>
        </div>
      ))}
    </div>
  );
}

function CourseDetailDialog({ course, bookings, isAdmin, qrLoading, onShowQr, onClose, onCancelBooking, weekDate, onAusfall, onAusfallAufheben, onEingetragen }) {
  const [ausfallGrund, setAusfallGrund] = useState('');
  const [ausfallOffen, setAusfallOffen] = useState(false);
  const terminDatum = (() => { try { const d = new Date(weekDate); d.setDate(d.getDate() + (course.day_index || 0)); return d.toISOString().slice(0, 10); } catch { return null; } })();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="course-detail-dialog">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-5 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">{course.name}</h3>
            <p className="text-sm text-muted-foreground">{course.day} &middot; {course.start} - {course.end} Uhr</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center justify-between py-2 px-3 bg-accent/50 rounded-lg mb-3">
          <span className="text-sm font-medium">Teilnehmer</span>
          <span className="text-sm font-bold">{bookings.length} / {course.max_spots}</span>
        </div>

        {bookings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Noch keine Buchungen</p>
        ) : (
          <div className="space-y-1.5">
            {bookings.map((b, idx) => (
              <div key={b.id} className="flex items-center justify-between py-2 px-3 bg-accent/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium">{b.user_name}</span>
                  {b.ueber_kontingent && (
                    <span className="text-[10px] rounded border border-amber-500 px-1 text-amber-700 dark:text-amber-400"
                          title="Liegt mit dieser Buchung über dem Wochenkontingent seines Tarifs" data-testid={`ueber-kontingent-${b.id}`}>
                      über Kontingent
                    </span>
                  )}
                </div>
                <button
                  onClick={() => onCancelBooking(b.id)}
                  className="text-xs text-destructive hover:underline"
                  data-testid={`admin-cancel-${b.id}`}
                >
                  Stornieren
                </button>
              </div>
            ))}
          </div>
        )}

        {isAdmin && !course.ausfall && <MitgliedEintragen course={course} weekDate={weekDate} onEingetragen={onEingetragen} />}

        {course.ausfall && (
          <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-700" data-testid="kurs-ausfall-hinweis">
            Fällt am {new Date(course.ausfall.datum).toLocaleDateString('de-DE')} aus{course.ausfall.grund ? ` — ${course.ausfall.grund}` : ''}.
            {isAdmin && <button type="button" className="ml-2 underline" onClick={() => onAusfallAufheben(course, course.ausfall.datum)}>Doch stattfinden lassen</button>}
          </div>
        )}
        {isAdmin && !course.ausfall && terminDatum && (
          <div className="mt-3">
            {!ausfallOffen ? (
              <button type="button" onClick={() => setAusfallOffen(true)} data-testid="kurs-ausfall-btn"
                className="w-full py-2 text-sm font-medium border border-red-500/40 text-red-600 rounded-lg hover:bg-red-500/10 transition-colors">
                Termin am {new Date(terminDatum).toLocaleDateString('de-DE')} fällt aus
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <input value={ausfallGrund} onChange={(e) => setAusfallGrund(e.target.value)} placeholder="Grund (Trainer krank, Turnier, Feiertag …)"
                  className="w-full text-sm rounded-md border border-border bg-background px-2 py-1.5" data-testid="kurs-ausfall-grund" />
                <p className="text-xs text-muted-foreground">{bookings.length} Buchung{bookings.length === 1 ? '' : 'en'} werden storniert, alle Gebuchten bekommen einen Push.</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setAusfallOffen(false)} className="flex-1 py-1.5 text-sm border border-border rounded-lg">Abbrechen</button>
                  <button type="button" onClick={() => onAusfall(course, terminDatum, ausfallGrund)} data-testid="kurs-ausfall-bestaetigen"
                    className="flex-1 py-1.5 text-sm font-medium bg-red-600 text-white rounded-lg">Ausfall melden</button>
                </div>
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <button
            onClick={onShowQr}
            disabled={qrLoading}
            data-testid="show-course-qr-btn"
            className="w-full mt-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {qrLoading ? 'Lädt…' : 'QR-Code für Check-in anzeigen'}
          </button>
        )}

        <button
          onClick={onClose}
          className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
        >
          Schließen
        </button>
      </div>
    </div>
  );
}
